import type { CustomFont } from './fontLoader';
import type { FontAlias } from '../stores/fontAliasStore';
import { normalizeFontName, tokenizeFontName, tokenOverlap } from './fontNameNormalize';

/**
 * Unified font-matching pipeline.
 *
 * Both import-time matching (svgFontMatcher) and runtime resolution
 * (fontResolution) share this single ordered pipeline so the two surfaces
 * never disagree about what a given CSS font-family string means.
 *
 * Order:
 *   1. exact       - css value equals an uploaded font.fontFamily.
 *   2. alias       - cs_svg_font_aliases row exists whose target family is uploaded.
 *   3. normalized  - after stripping case/space/punctuation.
 *   4. contains    - one normalized name is a substring of the other.
 *   5. token       - >= 60% tokens overlap (camelCase / hyphen split).
 *   6. filename    - css value equals a font file name (sans extension), normalized.
 *   7. miss        - null.
 *
 * The pipeline also parses PostScript-style suffixes ("MyFont-Bold") so the
 * family side of the candidate is tried against both the raw value and the
 * pre-hyphen root.
 */

export type MatchTier = 'exact' | 'alias' | 'normalized' | 'contains' | 'token' | 'filename' | 'manual';

export interface FontCandidate {
  tier: MatchTier;
  matchedFamily: string;
  matchedWeight: number | null;
  matchedStyle: string | null;
  aliasId?: string;
}

export interface ResolverContext {
  fonts: CustomFont[];
  aliases: FontAlias[];
}

function parseFallbackChain(cssValue: string): string[] {
  return cssValue
    .split(',')
    .map((v) => v.trim().replace(/^['"]|['"]$/g, ''))
    .filter((v) => v.length > 0);
}

function extractFamilyRoot(name: string): string {
  const lastHyphen = name.lastIndexOf('-');
  if (lastHyphen > 0) return name.substring(0, lastHyphen);
  return name;
}

export function resolveFontCandidate(
  cssValue: string,
  ctx: ResolverContext
): FontCandidate | null {
  const trimmed = (cssValue || '').trim();
  if (!trimmed) return null;

  const activeFonts = ctx.fonts.filter((f) => f.isActive);
  if (activeFonts.length === 0 && ctx.aliases.length === 0) return null;

  const familyToFonts = new Map<string, CustomFont[]>();
  const normalizedFamilyMap = new Map<string, string>();
  const fileNameMap = new Map<string, CustomFont>();

  for (const font of activeFonts) {
    const existing = familyToFonts.get(font.fontFamily) || [];
    existing.push(font);
    familyToFonts.set(font.fontFamily, existing);

    const nf = normalizeFontName(font.fontFamily);
    if (!normalizedFamilyMap.has(nf)) normalizedFamilyMap.set(nf, font.fontFamily);

    if (font.fileName) {
      const noExt = font.fileName.replace(/\.(ttf|otf|woff|woff2)$/i, '');
      fileNameMap.set(normalizeFontName(noExt), font);
    }
  }

  const allFamilyNames = Array.from(familyToFonts.keys());
  const candidates = parseFallbackChain(trimmed);

  for (const candidate of candidates) {
    if (familyToFonts.has(candidate)) {
      return plainMatch('exact', candidate);
    }
  }

  const exactAlias = ctx.aliases.find((a) => a.originalCss === trimmed);
  if (exactAlias && familyToFonts.has(exactAlias.assignedFamily)) {
    return aliasMatch(exactAlias);
  }

  const normTrimmed = normalizeFontName(trimmed);
  const normAlias = ctx.aliases.find((a) => normalizeFontName(a.originalCss) === normTrimmed);
  if (normAlias && familyToFonts.has(normAlias.assignedFamily)) {
    return aliasMatch(normAlias);
  }

  for (const candidate of candidates) {
    const root = extractFamilyRoot(candidate);
    if (familyToFonts.has(root)) return plainMatch('exact', root);

    const nc = normalizeFontName(candidate);
    const nr = normalizeFontName(root);
    const hit = normalizedFamilyMap.get(nc) ?? normalizedFamilyMap.get(nr);
    if (hit) return plainMatch('normalized', hit);

    for (const familyName of allFamilyNames) {
      const nf = normalizeFontName(familyName);
      if (nf.includes(nc) || nc.includes(nf) || nf.includes(nr) || nr.includes(nf)) {
        return plainMatch('contains', familyName);
      }
    }

    const candidateTokens = tokenizeFontName(candidate);
    let bestOverlap = 0;
    let bestFamily = '';
    for (const familyName of allFamilyNames) {
      const familyTokens = tokenizeFontName(familyName);
      const overlap = tokenOverlap(candidateTokens, familyTokens);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestFamily = familyName;
      }
    }
    if (bestOverlap >= 0.6 && bestFamily) {
      return plainMatch('token', bestFamily);
    }

    const fileHit = fileNameMap.get(nc) ?? fileNameMap.get(nr);
    if (fileHit) return plainMatch('filename', fileHit.fontFamily);
  }

  return null;
}

function plainMatch(tier: MatchTier, family: string): FontCandidate {
  return { tier, matchedFamily: family, matchedWeight: null, matchedStyle: null };
}

function aliasMatch(alias: FontAlias): FontCandidate {
  return {
    tier: 'alias',
    matchedFamily: alias.assignedFamily,
    matchedWeight: alias.assignedWeight,
    matchedStyle: alias.assignedStyle ?? 'normal',
    aliasId: alias.id,
  };
}
